import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit3, X, Check, Circle, AlertCircle,
  Building2, Users, Calendar, Filter, MessageCircle, Phone,
  ChevronLeft, ChevronRight, CalendarPlus, Download,
} from "lucide-react";
import { outlookComposeUrl, buildICS, downloadICS } from "@/lib/calendar";

/** Haftalık takvim yardımcı fonksiyonları */
const DAY_LABELS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTH_LABELS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

/** Verilen tarihin Pazartesi'sini döner (yerel saat). */
function startOfWeek(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay();                 // 0=Paz .. 6=Cmt
  const diff = day === 0 ? -6 : 1 - day;  // Pazartesi baz
  x.setDate(x.getDate() + diff);
  return x;
}
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

const PRIORITY = {
  low: { label: "Düşük", color: "text-neutral-600 border-neutral-300 bg-neutral-50" },
  medium: { label: "Orta", color: "text-amber-700 border-amber-300 bg-amber-50" },
  high: { label: "Yüksek", color: "text-rose-700 border-rose-300 bg-rose-50" },
};

/** TR telefonu wa.me formatına çevirir. "0532 123 45 67" → "905321234567" */
export function toWaPhone(raw) {
  if (!raw) return "";
  let d = String(raw).replace(/\D+/g, "");
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("0")) d = d.slice(1);
  if (d.length === 10) d = "90" + d;
  return d;
}

/** Todo için WhatsApp mesaj metni (personele iletim için). */
export function buildTodoMessage(todo, contact, account) {
  const P = PRIORITY[todo.priority]?.label || "Orta";
  const parts = [`✅ *Görev:* ${todo.title}`, ""];
  if (todo.description) parts.push(`📋 ${todo.description}`, "");
  if (todo.due_date) parts.push(`⏰ *Vade:* ${fmtDate(todo.due_date)}`);
  parts.push(`🚦 *Öncelik:* ${P}`);
  if (contact) parts.push(`👤 *İlgili Cari:* ${contact.name}`);
  if (account) parts.push(`🏦 *İlgili Hesap:* ${account.name}`);
  parts.push("", "_Plasper Finans — ekip görevi_");
  return parts.join("\n");
}

/** Personel seçici + WhatsApp paylaşım popover'ı. */
export function TodoWhatsAppShare({ todo, contact, account }) {
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState(null);   // null = henüz yüklenmedi
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Popover açıldığında personeli lazy yükle
  useEffect(() => {
    if (!open || employees !== null) return;
    setLoading(true);
    api.get("/employees").then(({ data }) => {
      setEmployees((data || []).filter((e) => e.active !== false));
    }).catch(() => setEmployees([])).finally(() => setLoading(false));
  }, [open, employees]);

  // Dışarı tıklama & Escape ile kapat
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const message = encodeURIComponent(buildTodoMessage(todo, contact, account));
  const genericUrl = `https://wa.me/?text=${message}`;   // fallback: alıcı yok, WhatsApp seçici açılır

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid={`todo-whatsapp-${todo.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="p-1.5 hover:bg-emerald-50"
        title="Personelle WhatsApp'ta paylaş"
      >
        <MessageCircle size={13} className="text-emerald-600" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-40 w-64 bg-white border-2 border-black shadow-[6px_6px_0_0_rgba(0,0,0,0.9)]"
          onClick={(e) => e.stopPropagation()}
          data-testid={`todo-whatsapp-menu-${todo.id}`}
        >
          <div className="px-3 py-2 border-b-2 border-black flex items-center justify-between">
            <span className="label-caps text-[10px]">Personel Seç</span>
            <button onClick={() => setOpen(false)} className="p-0.5"><X size={12} /></button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading && (
              <div className="p-3 text-xs text-neutral-500 text-center">Yükleniyor…</div>
            )}
            {!loading && employees && employees.length === 0 && (
              <div className="p-3 text-xs text-neutral-500 text-center">
                Kayıtlı aktif personel yok.
              </div>
            )}
            {!loading && employees && employees.map((e) => {
              const phone = toWaPhone(e.phone);
              const disabled = !phone;
              return (
                <a
                  key={e.id}
                  href={disabled ? undefined : `https://wa.me/${phone}?text=${message}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`todo-whatsapp-emp-${todo.id}-${e.id}`}
                  onClick={(ev) => {
                    if (disabled) { ev.preventDefault(); toast.error(`${e.name} için telefon kayıtlı değil`); return; }
                    setOpen(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-2 border-b border-black/5 last:border-b-0 text-xs ${
                    disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-emerald-50 cursor-pointer"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.name}</div>
                    {e.position && <div className="text-[10px] text-neutral-500 truncate">{e.position}</div>}
                  </div>
                  {phone ? (
                    <span className="font-mono-num text-[10px] text-neutral-600 flex items-center gap-1">
                      <Phone size={9} /> {e.phone}
                    </span>
                  ) : (
                    <span className="text-[10px] text-rose-500">Telefon yok</span>
                  )}
                </a>
              );
            })}
          </div>
          <a
            href={genericUrl}
            target="_blank" rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid={`todo-whatsapp-generic-${todo.id}`}
            className="block px-3 py-2 border-t-2 border-black bg-neutral-50 hover:bg-emerald-50 text-xs text-center"
          >
            🔗 WhatsApp'ı aç, alıcıyı orada seç
          </a>
        </div>
      )}
    </div>
  );
}

export default function Todos() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("open");
  const [linkFilter, setLinkFilter] = useState("all"); // all | contact | account | none
  const [dayFilter, setDayFilter] = useState(null);    // null | "YYYY-MM-DD"
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [{ data: ts }, { data: co }, { data: ac }] = await Promise.all([
      api.get("/todos"), api.get("/contacts"), api.get("/accounts"),
    ]);
    setItems(ts); setContacts(co); setAccounts(ac);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id) => {
    await api.post(`/todos/${id}/toggle`);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Görev silinsin mi?")) return;
    await api.delete(`/todos/${id}`);
    toast.success("Silindi"); load();
  };

  const today = todayISO();
  const filtered = items.filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (linkFilter === "contact" && !t.contact_id) return false;
    if (linkFilter === "account" && !t.account_id) return false;
    if (linkFilter === "none" && (t.contact_id || t.account_id)) return false;
    if (dayFilter && t.due_date !== dayFilter) return false;
    return true;
  });

  const openCount = items.filter((t) => t.status === "open").length;
  const overdueCount = items.filter((t) => t.status === "open" && t.due_date && t.due_date < today).length;
  const doneCount = items.filter((t) => t.status === "done").length;

  const exportICS = () => {
    const list = filtered.filter((t) => t.due_date);
    if (!list.length) { toast.error("Son tarihi olan görev yok"); return; }
    const entries = list.map((t) => ({
      todo: t,
      contact: contacts.find((c) => c.id === t.contact_id),
      account: accounts.find((a) => a.id === t.account_id),
    }));
    downloadICS(buildICS(entries), `yapilacaklar-${today}.ics`);
    toast.success(`${list.length} görev .ics olarak indirildi`);
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Görevler ve Takip</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Yapılacaklar</h1>
        </div>
        <div className="flex gap-2">
        <button
          data-testid="todo-export-ics-button"
          onClick={exportICS}
          title="Listede görünen, son tarihi olan görevleri Outlook'a aktarılabilir .ics dosyası olarak indir"
          className="border-2 border-black bg-white px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-sky-50"
        >
          <Download size={16} /> Outlook'a Aktar (.ics)
        </button>
        <button
          data-testid="todo-add-button"
          onClick={() => { setEditing(null); setOpen(true); }}
          className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2"
        >
          <Plus size={16} /> Yeni Görev
        </button>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <MiniStat label="Açık" value={openCount} tid="stat-todo-open" />
        <MiniStat label="Gecikmiş" value={overdueCount} tone={overdueCount > 0 ? "rose" : "black"} tid="stat-todo-overdue" />
        <MiniStat label="Tamamlanan" value={doneCount} tone="emerald" tid="stat-todo-done" />
      </div>

      <WeeklyCalendar
        items={items}
        weekStart={weekStart}
        setWeekStart={setWeekStart}
        dayFilter={dayFilter}
        setDayFilter={setDayFilter}
        today={today}
      />

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex flex-wrap gap-1.5">
          {[["open", "Açık"], ["done", "Tamamlanan"], ["all", "Tümü"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`todo-filter-${k}`}
              onClick={() => setStatusFilter(k)}
              className={`text-[11px] uppercase tracking-widest px-2.5 py-1 border ${
                statusFilter === k ? "bg-black text-white border-black" : "border-black/15 text-neutral-600 hover:border-black/40"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="mx-1 h-4 w-px bg-black/20" />
        <div className="flex flex-wrap gap-1.5">
          {[["all", "Tüm Bağlantılar"], ["contact", "Cariye Bağlı"], ["account", "Bankaya Bağlı"], ["none", "Bağlantısız"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`todo-linkfilter-${k}`}
              onClick={() => setLinkFilter(k)}
              className={`text-[11px] uppercase tracking-widest px-2.5 py-1 border ${
                linkFilter === k ? "bg-neutral-900 text-white border-neutral-900" : "border-black/15 text-neutral-600 hover:border-black/40"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="brutal-card overflow-hidden" data-testid="todo-list">
        {filtered.length === 0 ? (
          <div className="text-center py-14 text-neutral-500 text-sm">
            Görev yok. "Yeni Görev" ile başlayın.
          </div>
        ) : (
          <ul className="divide-y divide-black/5">
            {filtered.map((t) => (
              <TodoRow
                key={t.id} todo={t}
                contact={contacts.find((c) => c.id === t.contact_id)}
                account={accounts.find((a) => a.id === t.account_id)}
                onToggle={() => toggle(t.id)}
                onEdit={() => { setEditing(t); setOpen(true); }}
                onRemove={() => remove(t.id)}
              />
            ))}
          </ul>
        )}
      </div>

      {open && (
        <TodoModal
          editing={editing} contacts={contacts} accounts={accounts}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function WeeklyCalendar({ items, weekStart, setWeekStart, dayFilter, setDayFilter, today }) {
  // Haftanın 7 günü (Pzt → Paz)
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i);
    return { date: d, iso: toISO(d) };
  });

  // Gün başına görev sayısı ve durum kırılımları
  const buckets = {};
  for (const t of items) {
    if (!t.due_date) continue;
    const b = buckets[t.due_date] || (buckets[t.due_date] = { open: 0, done: 0, high: 0, overdue: 0 });
    if (t.status === "done") b.done++; else b.open++;
    if (t.status === "open" && t.priority === "high") b.high++;
    if (t.status === "open" && t.due_date < today) b.overdue++;
  }

  const start = days[0].date;
  const end = days[6].date;
  const rangeLabel = start.getMonth() === end.getMonth()
    ? `${start.getDate()} – ${end.getDate()} ${MONTH_LABELS[start.getMonth()]} ${end.getFullYear()}`
    : `${start.getDate()} ${MONTH_LABELS[start.getMonth()]} – ${end.getDate()} ${MONTH_LABELS[end.getMonth()]} ${end.getFullYear()}`;

  const jumpWeeks = (n) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + n * 7);
    setWeekStart(d);
  };
  const goToday = () => setWeekStart(startOfWeek(new Date()));

  return (
    <div className="brutal-card p-4" data-testid="todo-weekly-calendar">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-neutral-500" />
          <span className="label-caps">Haftalık Takvim</span>
          <span className="text-xs text-neutral-500 font-mono-num ml-2">{rangeLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          {dayFilter && (
            <button
              data-testid="todo-calendar-clear"
              onClick={() => setDayFilter(null)}
              className="text-[10px] uppercase tracking-widest px-2 py-1 border border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100"
            >
              Filtreyi Temizle
            </button>
          )}
          <button
            data-testid="todo-calendar-prev"
            onClick={() => jumpWeeks(-1)}
            className="p-1.5 border border-black/15 hover:border-black"
            title="Önceki hafta"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            data-testid="todo-calendar-today"
            onClick={goToday}
            className="text-[10px] uppercase tracking-widest px-2 py-1 border border-black/15 hover:border-black"
          >
            Bugün
          </button>
          <button
            data-testid="todo-calendar-next"
            onClick={() => jumpWeeks(1)}
            className="p-1.5 border border-black/15 hover:border-black"
            title="Sonraki hafta"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {days.map((d, idx) => {
          const b = buckets[d.iso];
          const total = (b?.open || 0) + (b?.done || 0);
          const isToday = d.iso === today;
          const isSelected = dayFilter === d.iso;
          const hasOverdue = (b?.overdue || 0) > 0;
          const hasHigh = (b?.high || 0) > 0;

          const base = "relative flex flex-col items-center justify-start p-2 border transition text-left cursor-pointer min-h-[74px]";
          let toneClass = "border-black/10 hover:border-black/40 bg-white";
          if (isSelected) toneClass = "border-black bg-black text-white shadow-[3px_3px_0_0_rgba(0,0,0,0.9)]";
          else if (hasOverdue) toneClass = "border-rose-300 bg-rose-50 hover:border-rose-500";
          else if (total > 0) toneClass = "border-amber-300 bg-amber-50 hover:border-amber-500";

          return (
            <button
              key={d.iso}
              data-testid={`todo-calendar-day-${d.iso}`}
              onClick={() => setDayFilter(isSelected ? null : d.iso)}
              className={`${base} ${toneClass}`}
            >
              <div className="flex items-center justify-between w-full">
                <span className={`text-[10px] uppercase tracking-widest ${isSelected ? "text-white/70" : "text-neutral-500"}`}>
                  {DAY_LABELS[idx]}
                </span>
                {isToday && (
                  <span className={`text-[9px] font-bold px-1 border ${
                    isSelected ? "border-white/40 text-white" : "border-black text-black"
                  }`}>
                    BUGÜN
                  </span>
                )}
              </div>
              <div className={`font-mono-num text-xl font-semibold mt-1 ${isSelected ? "text-white" : ""}`}>
                {d.date.getDate()}
              </div>
              {total > 0 ? (
                <div className="mt-auto pt-1 flex items-center gap-1 flex-wrap">
                  <span className={`text-[10px] font-mono-num px-1.5 py-0.5 border ${
                    isSelected ? "border-white/40 text-white"
                    : hasOverdue ? "border-rose-400 text-rose-700 bg-white"
                    : "border-black/30 text-neutral-700 bg-white"
                  }`}>
                    {total} görev
                  </span>
                  {hasHigh && !isSelected && (
                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500" title="Yüksek öncelikli görev var" />
                  )}
                </div>
              ) : (
                <div className={`mt-auto text-[10px] ${isSelected ? "text-white/50" : "text-neutral-300"}`}>—</div>
              )}
            </button>
          );
        })}
      </div>

      {dayFilter && (
        <div className="mt-3 text-[11px] text-neutral-600 border-l-2 border-black pl-3">
          Sadece <span className="font-mono-num font-semibold">{fmtDate(dayFilter)}</span> tarihli görevler gösteriliyor.
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone, tid }) {
  const color = tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-700" : "";
  return (
    <div className="brutal-card p-5" data-testid={tid}>
      <div className="label-caps">{label}</div>
      <div className={`font-mono-num text-3xl font-semibold mt-2 ${color}`}>{value}</div>
    </div>
  );
}

export function TodoRow({ todo, contact, account, onToggle, onEdit, onRemove, dense }) {
  const overdue = todo.status === "open" && todo.due_date && todo.due_date < todayISO();
  const P = PRIORITY[todo.priority];
  return (
    <li
      data-testid={`todo-row-${todo.id}`}
      className={`flex items-start gap-3 ${dense ? "p-3" : "p-4"} ${todo.status === "done" ? "opacity-60" : ""}`}
    >
      <button
        data-testid={`todo-toggle-${todo.id}`}
        onClick={onToggle}
        className={`mt-0.5 w-5 h-5 border-2 flex items-center justify-center flex-shrink-0 ${
          todo.status === "done" ? "bg-black border-black text-white" : "border-black/40 hover:border-black"
        }`}
      >
        {todo.status === "done" ? <Check size={12} strokeWidth={3} /> : null}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${todo.status === "done" ? "line-through" : ""}`}>
          {todo.title}
        </div>
        {todo.description && !dense && (
          <div className="text-xs text-neutral-500 mt-0.5 line-clamp-2">{todo.description}</div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className={`border px-1.5 py-0.5 uppercase tracking-widest ${P.color}`}>{P.label}</span>
          {todo.due_date && (
            <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 ${
              overdue ? "border-rose-300 text-rose-700 bg-rose-50" : "border-black/15 text-neutral-600"
            }`}>
              <Calendar size={10} /> {fmtDate(todo.due_date)}
              {overdue && <AlertCircle size={10} />}
            </span>
          )}
          {contact && (
            <span className="inline-flex items-center gap-1 border border-black/15 text-neutral-700 px-1.5 py-0.5">
              <Users size={10} /> {contact.name}
            </span>
          )}
          {account && (
            <span className="inline-flex items-center gap-1 border border-black/15 text-neutral-700 px-1.5 py-0.5">
              <Building2 size={10} /> {account.name}
            </span>
          )}
        </div>
      </div>
      {(onEdit || onRemove) && (
        <div className="flex gap-0.5 flex-shrink-0">
          <TodoWhatsAppShare todo={todo} contact={contact} account={account} />
          <button
            data-testid={`todo-outlook-${todo.id}`}
            onClick={() => {
              if (!todo.due_date) { toast.error("Outlook'a eklemek için önce son tarih girin"); return; }
              window.open(outlookComposeUrl(todo, contact, account), "_blank", "noopener,noreferrer");
            }}
            className={`p-1.5 hover:bg-sky-50 ${todo.due_date ? "" : "opacity-40"}`}
            title={todo.due_date ? "Outlook takvimine ekle" : "Son tarih yok"}
          >
            <CalendarPlus size={13} className="text-sky-700" />
          </button>
          {onEdit && (
            <button onClick={onEdit} className="p-1.5 hover:bg-neutral-100"><Edit3 size={13} /></button>
          )}
          {onRemove && (
            <button data-testid={`todo-delete-${todo.id}`} onClick={onRemove} className="p-1.5 hover:bg-rose-50">
              <Trash2 size={13} className="text-rose-600" />
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function TodoModal({ editing, contacts, accounts, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    title: "", description: "",
    due_date: "", priority: "medium",
    contact_id: "", account_id: "",
    status: "open",
  });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      due_date: form.due_date || null,
      contact_id: form.contact_id || null,
      account_id: form.account_id || null,
      description: form.description || null,
    };
    try {
      if (editing) await api.put(`/todos/${editing.id}`, payload);
      else await api.post("/todos", payload);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      onSaved();
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        data-testid="todo-form-modal"
        className="bg-white border border-black w-full max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">{editing ? "Görevi Düzenle" : "Yeni Görev"}</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <F label="Başlık">
            <input required data-testid="todo-title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="input" placeholder="Örn: X A.Ş. faturası için mutabakat yap" />
          </F>

          <F label="Açıklama (opsiyonel)">
            <textarea rows={2} data-testid="todo-description" value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="input" />
          </F>

          <div className="grid grid-cols-2 gap-3">
            <F label="Son Tarih">
              <input type="date" data-testid="todo-due-date" value={form.due_date || ""}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="input" />
            </F>
            <F label="Öncelik">
              <div className="grid grid-cols-3 gap-1">
                {Object.entries(PRIORITY).map(([k, v]) => (
                  <button
                    key={k} type="button"
                    data-testid={`todo-priority-${k}`}
                    onClick={() => setForm({ ...form, priority: k })}
                    className={`text-[11px] uppercase tracking-widest py-2 border ${
                      form.priority === k ? "bg-black text-white border-black" : "border-black/15 hover:border-black/40"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </F>
          </div>

          <F label="İlgili Cari (opsiyonel)">
            <select data-testid="todo-contact" value={form.contact_id || ""}
              onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
              className="input bg-white">
              <option value="">— Yok —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>

          <F label="İlgili Banka / Hesap (opsiyonel)">
            <select data-testid="todo-account" value={form.account_id || ""}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              className="input bg-white">
              <option value="">— Yok —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </F>

          <div className="text-[11px] text-neutral-500 border-l-2 border-black pl-3 leading-relaxed">
            Görev bir cari veya banka hesabıyla ilişkilendirildiğinde ilgili sayfada / raporlarda daha kolay bulunur.
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="todo-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>;
}
